/**
 * useBreakdown Hook
 *
 * Drives the review of one intake's AI-generated breakdown (FLOW-549):
 * loading it, generating it if it doesn't exist yet, negotiating revisions,
 * tracking which stories the user has rejected, and committing the approved
 * ones. Mirrors useIntake's busy/error/optimistic-echo shape closely — this
 * is the same session pattern one step further down the Plan flow.
 *
 * Rejection is client-side state (see the design doc's "Where approval state
 * lives"): a story is approved unless its id is in `rejectedStoryIds`. That
 * set must survive a negotiation turn, which can reorder, insert, or drop
 * stories — so after every turn it is reconciled against the ids the revised
 * proposal actually contains, dropping rejections for stories that are gone
 * and keeping the rest.
 */

import { Accessor, createEffect, createMemo, createSignal, on } from "solid-js";
import type {
  InitiativeBreakdown,
  IntakeId,
  Message,
  ProposedStory,
} from "../api/types";
import {
  commitBreakdown,
  discardBreakdown,
  generateBreakdown,
  getBreakdown,
  sendBreakdownTurn,
  waitForBreakdown,
  type CommitBreakdownResponse,
} from "../api/breakdown";
import { getConversation } from "../api/conversations";
import { ApiError } from "../api/client";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";

export interface UseBreakdownResult {
  /** The breakdown being reviewed, if one has been generated */
  breakdown: Accessor<InitiativeBreakdown | undefined>;
  /** True once the initial lookup for the current intake has settled — lets
   *  the caller distinguish "still checking" from "definitely none yet" */
  checked: Accessor<boolean>;
  /** Negotiation transcript for the current breakdown */
  messages: Accessor<Message[]>;
  /** Message awaiting a reply, shown optimistically while the AI works */
  pending: Accessor<string | null>;
  /** True while a request is in flight */
  busy: Accessor<boolean>;
  /** Human-readable failure from the last action, if it failed */
  error: Accessor<string | null>;
  /** Ids of stories the user has turned down. Included but not deleted —
   *  rejected stories stay visible until committed. */
  rejectedStoryIds: Accessor<Set<string>>;
  /** Generate a fresh breakdown from the intake's assembled spec */
  generate: () => Promise<boolean>;
  /** Ask the AI to add, remove, split, or reframe stories */
  negotiate: (content: string) => Promise<boolean>;
  /** Flip a story's approval state */
  toggleStory: (id: string) => void;
  /** Stories that are not currently rejected, in payload order */
  approvedStories: Accessor<ProposedStory[]>;
  approvedCount: Accessor<number>;
  /** Sum of approved stories' estimate values (same unit across the breakdown) */
  totalEstimate: Accessor<number>;
  /** Commit the approved stories. Resolves null if there was nothing to commit or it failed. */
  commit: () => Promise<CommitBreakdownResponse | null>;
  /** Throw the proposal away, leaving the spec to generate a new one from */
  discard: () => Promise<boolean>;
  /** Dismiss the current error */
  clearError: () => void;
}

export function useBreakdown(
  intakeId: Accessor<IntakeId | undefined>,
): UseBreakdownResult {
  const [breakdown, setBreakdown] = createSignal<InitiativeBreakdown>();
  const [checked, setChecked] = createSignal(false);
  const [messages, setMessages] = createSignal<Message[]>([]);
  const [pending, setPending] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [rejectedStoryIds, setRejectedStoryIds] = createSignal<Set<string>>(
    new Set(),
  );

  const clear = () => {
    setBreakdown(undefined);
    setChecked(false);
    setMessages([]);
    setPending(null);
    setError(null);
    setRejectedStoryIds(new Set<string>());
  };

  /**
   * `forId` is the intake this thread load is for — both `load` and `run`
   * pass their own captured `id`. `getConversation` is a real network round
   * trip, so by the time it resolves the caller may have switched to a
   * different intake — checked fresh against `intakeId()` (never the stale
   * `forId` itself) before applying the result.
   */
  const loadThread = async (
    row: InitiativeBreakdown,
    forId: IntakeId | undefined,
  ) => {
    const stillCurrent = () => forId === intakeId();
    if (!row.conversation_id) {
      if (stillCurrent()) setMessages([]);
      return;
    }
    const conversation = await getConversation(row.conversation_id);
    if (stillCurrent()) setMessages(conversation.messages);
  };

  /** Drop rejections for stories the revised proposal no longer contains. */
  const reconcileRejections = (row: InitiativeBreakdown) => {
    const liveIds = new Set(
      row.payload.groups.flatMap((group) => group.stories).map((s) => s.id),
    );
    setRejectedStoryIds((prev) => {
      const next = new Set<string>();
      for (const id of prev) {
        if (liveIds.has(id)) next.add(id);
      }
      return next;
    });
  };

  /**
   * Run a mutation (`generate`/`negotiate`) with the shared
   * busy/error/optimistic-echo handling. `action` is a real AI round trip —
   * `generateBreakdown` can take up to 180s — so by the time it resolves the
   * caller may have switched intakes. `id` is captured once at entry (both
   * callers only invoke `run` for the intake that is current at the moment
   * of the call) and re-read fresh via `intakeId()` after `action()`
   * resolves, after the `loadThread` that follows it, and in `catch` —
   * never the stale captured `id` itself, per FLOW-814. A stale call bails
   * without touching `breakdown`/`messages`/`error`/`pending`/`busy` — the
   * newer session (either the current `run` or the `load` a switch
   * triggers) owns those now.
   */
  const run = async (
    echo: string | null,
    action: () => Promise<InitiativeBreakdown>,
  ): Promise<boolean> => {
    const id = intakeId();
    setBusy(true);
    setError(null);
    setPending(echo);
    try {
      const updated = await action();
      if (id !== intakeId()) return false;
      setBreakdown(updated);
      reconcileRejections(updated);
      await loadThread(updated, id);
      if (id !== intakeId()) return false;
      return true;
    } catch (err) {
      if (id !== intakeId()) return false;
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      return false;
    } finally {
      if (id === intakeId()) {
        setPending(null);
        setBusy(false);
      }
    }
  };

  /**
   * `waitForBreakdown` can take up to 180s, `getBreakdown` and
   * `getConversation` (inside `loadThread`) are each a real network round
   * trip — any of them can outlive the session it was started for if the
   * caller switches intakes in the meantime. Every read of `intakeId()`
   * below (including inside `loadThread`, via the `id` passed to it) is
   * fresh, never the `id` this call closed over, per FLOW-814: a post-await
   * signal read is exactly where a stale-callback bug hides. A stale `load`
   * bails silently rather than touching state — the intake it was for is no
   * longer the one on screen, and the newer `load` for the current intake
   * owns `checked`/`busy`/`error`/`breakdown`/`messages` now.
   */
  const load = async (id: IntakeId) => {
    setBusy(true);
    setError(null);
    try {
      const lookup = await getBreakdown(id);
      if (id !== intakeId()) return;

      let row: InitiativeBreakdown;
      if (lookup.status === "ready") {
        row = lookup.breakdown;
      } else {
        setChecked(true); // the lookup has settled: a generation is running
        row = await waitForBreakdown(id);
        if (id !== intakeId()) return;
      }
      setBreakdown(row);
      reconcileRejections(row);
      await loadThread(row, id);
    } catch (err) {
      if (id !== intakeId()) return;
      // No breakdown yet is not a failure — it's the state that offers "Generate".
      if (!(err instanceof ApiError && err.status === 404)) {
        surfaceQuotaExhausted(err);
        setError(describeAiError(err));
      }
    } finally {
      if (id === intakeId()) {
        setChecked(true);
        setBusy(false);
      }
    }
  };

  const generate = (): Promise<boolean> => {
    const id = intakeId();
    if (!id) return Promise.resolve(false);
    return run(null, () => generateBreakdown(id));
  };

  const negotiate = (content: string): Promise<boolean> => {
    const current = breakdown();
    if (!current) return Promise.resolve(false);
    return run(content, async () => {
      const response = await sendBreakdownTurn(
        current.intake_id,
        content,
        Array.from(rejectedStoryIds()),
      );
      return response.breakdown;
    });
  };

  const toggleStory = (id: string) => {
    setRejectedStoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const approvedStories = createMemo(() => {
    const row = breakdown();
    if (!row) return [];
    const rejected = rejectedStoryIds();
    return row.payload.groups
      .flatMap((group) => group.stories)
      .filter((story) => !rejected.has(story.id));
  });

  const approvedCount = createMemo(() => approvedStories().length);

  const totalEstimate = createMemo(() =>
    approvedStories().reduce((sum, story) => sum + story.estimate.value, 0),
  );

  const commit = async (): Promise<CommitBreakdownResponse | null> => {
    const current = breakdown();
    if (!current) return null;
    const storyIds = approvedStories().map((story) => story.id);
    if (storyIds.length === 0) return null;

    setBusy(true);
    setError(null);
    try {
      const result = await commitBreakdown(current.intake_id, storyIds);
      setBreakdown((prev) =>
        prev
          ? {
              ...prev,
              committed_at: new Date().toISOString(),
              epic_work_item_id: result.epics[0]?.id ?? null,
            }
          : prev,
      );
      return result;
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      return null;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Throw the proposal away (FLOW-641). Unlike `clear`, this leaves `checked`
   * alone: the lookup for this intake has settled and its answer is now
   * "none", which is the state that offers "Generate breakdown". Resetting it
   * would strand the surface on the "checking for an existing breakdown"
   * spinner with nothing left to check.
   */
  const discard = async (): Promise<boolean> => {
    const current = breakdown();
    if (!current) return false;

    setBusy(true);
    setError(null);
    try {
      await discardBreakdown(current.intake_id);
      setBreakdown(undefined);
      setMessages([]);
      setRejectedStoryIds(new Set<string>());
      return true;
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Load (or clear) the breakdown whenever the intake we're reviewing changes.
  createEffect(
    on(intakeId, (id) => {
      clear();
      if (id) void load(id);
    }),
  );

  return {
    breakdown,
    checked,
    messages,
    pending,
    busy,
    error,
    rejectedStoryIds,
    generate,
    negotiate,
    toggleStory,
    approvedStories,
    approvedCount,
    totalEstimate,
    commit,
    discard,
    clearError: () => setError(null),
  };
}
